import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';

interface LoadingSkeletonProps {
  type?: 'table' | 'cards' | 'detail' | 'form' | 'custom';
  rows?: number;
  cols?: number;
}

function SkeletonBox({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-muted/60 rounded ${className}`} />;
}

export default function LoadingSkeleton({
  type = 'table',
  rows = 5,
  cols = 4,
}: LoadingSkeletonProps) {
  if (type === 'cards') {
    return (
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} gap-4 animate-pulse`}
      >
        {Array.from({ length: cols }).map((_, i) => (
          <Card key={i} className="border border-border/60 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <SkeletonBox className="h-3 w-2/3" />
              <SkeletonBox className="h-8 w-1/2" />
              <SkeletonBox className="h-3 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (type === 'detail') {
    return (
      <div className="space-y-4">
        <SkeletonBox className="h-8 w-64" />
        <SkeletonBox className="h-4 w-96" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-2">
                <SkeletonBox className="h-3 w-1/2" />
                <SkeletonBox className="h-6 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonBox key={i} className="h-4 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (type === 'form') {
    return (
      <Card>
        <CardContent className="p-6 space-y-6">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="space-y-2">
              <SkeletonBox className="h-3 w-32" />
              <SkeletonBox className="h-10 w-full" />
            </div>
          ))}
          <SkeletonBox className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-5 w-40" />
          <div className="flex gap-2">
            <SkeletonBox className="h-8 w-32" />
            <SkeletonBox className="h-8 w-24" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
          >
            {Array.from({ length: cols }).map((_, j) => (
              <SkeletonBox key={j} className="h-4" />
            ))}
          </div>
          {Array.from({ length: rows }).map((_, i) => (
            <div
              key={i}
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
            >
              {Array.from({ length: cols }).map((_, j) => (
                <SkeletonBox key={j} className="h-5" />
              ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
